import { useState, useEffect, useRef } from 'react';
import framework from 'utils/framework';
const { wrapFunctionalComponent } = framework;
import LanguageLocaleUtils from 'utils/LanguageLocale';
import userUtils from 'utils/User';
import {
    Box, Text, Button, Link, Grid, Divider
} from 'components/ui';
import InputEmail from 'components/Inputs/InputEmail/InputEmail';
import PasswordRevealInput from 'components/Inputs/PasswordRevealInput';
import ErrorList from 'components/ErrorList';
import Storage from 'utils/localStorage/Storage';
import LOCAL_STORAGE from 'utils/localStorage/Constants';
import externalAuthService from 'services/api/authentication/externalAuth';
import UserActions from 'actions/UserActions';
import store from 'Store';
import Actions from 'Actions';
import Chevron from 'components/Chevron/Chevron';
import LoginEN from './locales/Login_en_US';
import LoginFR from './locales/Login_fr_CA';

const getText = LanguageLocaleUtils.getLocalisedDataFromResource(LoginEN, LoginFR);

/**
 * Login Page
 * Full-screen login page that handles both regular login and OAuth2 external authentication
 *
 * OAuth2 Flow (when client_id and redirect_uri params present):
 * 1. User is redirected here from backend /oauth2/authorize endpoint (302 redirect)
 * 2. URL contains OAuth2 protocol parameters for external authentication
 * 3. User enters email and password
 * 4. Submit credentials to backend /oauth2/login API endpoint
 * 5. On success: receive session_id, token, hasIdentity
 * 6. Trigger standard Sephora login flow using UserActions.signIn
 * 7. After full login completes, navigate to /authorize page for authorization
 *
 * Regular Login Flow (when OAuth2 params not present):
 * 1. User enters email and password
 * 2. Call UserActions.signIn directly with regular login callback
 * 3. Redirect to home page or intended destination after successful login
 */
const Login = () => {
    const emailInput = useRef(null);
    const passwordInput = useRef(null);

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [errorMessages, setErrorMessages] = useState([]);
    const [externalAuthParams, setExternalAuthParams] = useState({
        responseType: '',
        clientId: '',
        redirectUri: '',
        scope: '',
        state: '',
        codeChallenge: '',
        codeChallengeMethod: '',
        nonce: '',
        prompt: '',
        display: '',
        maxAge: '',
        uiLocales: '',
        idTokenHint: '',
        loginHint: '',
        acrValues: ''
    });

    // Determine if this is an OAuth2 flow based on presence of required OAuth2 parameters
    const isOAuth2Flow = !!(externalAuthParams.clientId && externalAuthParams.redirectUri);

    // Populate email and external auth params from URL on client-side
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);

        const params = {
            responseType: urlParams.get('response_type') || '',
            clientId: urlParams.get('client_id') || '',
            redirectUri: urlParams.get('redirect_uri') || '',
            scope: urlParams.get('scope') || '',
            state: urlParams.get('state') || '',
            codeChallenge: urlParams.get('code_challenge') || '',
            codeChallengeMethod: urlParams.get('code_challenge_method') || '',
            nonce: urlParams.get('nonce') || '',
            prompt: urlParams.get('prompt') || '',
            display: urlParams.get('display') || '',
            maxAge: urlParams.get('max_age') || '',
            uiLocales: urlParams.get('ui_locales') || '',
            idTokenHint: urlParams.get('id_token_hint') || '',
            loginHint: urlParams.get('login_hint') || '',
            acrValues: urlParams.get('acr_values') || ''
        };

        setExternalAuthParams(params);

        // Pre-populate email if login_hint is provided
        if (params.loginHint) {
            setEmail(params.loginHint);
        }

        // Store external auth params in localStorage for use after login (OAuth2 flow only)
        if (params.clientId && params.redirectUri) {
            Storage.local.setItem(LOCAL_STORAGE.EXTERNAL_AUTH_PARAMS, params);
        }
    }, []);

    const handleEmailChange = e => {
        setEmail(e.target.value);
    };

    const handlePasswordChange = e => {
        setPassword(e.target.value);
    };

    const validateForm = () => {
        const errors = [];

        if (!email || email.trim() === '') {
            errors.push({ errorMessage: 'Please enter your email address' });
        }

        if (!password || password.trim() === '') {
            errors.push({ errorMessage: 'Please enter your password' });
        }

        // For OAuth2 flow, validate required external auth parameters
        if (isOAuth2Flow) {
            if (!externalAuthParams.clientId) {
                errors.push({ errorMessage: 'Missing required parameter: client_id' });
            }

            if (!externalAuthParams.redirectUri) {
                errors.push({ errorMessage: 'Missing required parameter: redirect_uri' });
            }
        }

        setErrorMessages(errors);

        return errors.length === 0;
    };

    const handleSubmit = async e => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        store.dispatch(Actions.showInterstice(true));
        setErrorMessages([]);

        try {
            if (isOAuth2Flow) {
                // OAuth2 External Auth Flow - Call external auth login endpoint
                await handleExternalAuthLogin();
            } else {
                // Regular Login Flow - Call standard login directly
                await handleRegularLogin();
            }
        } catch (error) {
            const errorMessage = error?.errorMessage || error?.message || 'Login failed. Please try again.';

            setErrorMessages([{ errorMessage }]);
            store.dispatch(Actions.showInterstice(false));
        }
    };

    /**
     * Handle regular login flow
     * Standard Sephora login without OAuth2 external authentication
     */
    const handleRegularLogin = async () => {
        const successCallback = () => {
            // After successful login, redirect to home page or intended destination
            store.dispatch(Actions.showInterstice(false));
            window.location.href = '/';
        };

        const errorCallback = json => {
            if (json.errorMessages) {
                throw new Error(json.errorMessages[0]);
            }
        };

        await store.dispatch(
            UserActions.signIn(email, password, !!Sephora.isMobile(), false, successCallback, errorCallback, false, null, false, false, false, false)
        );
    };

    /**
     * Handle external auth login flow
     * Authenticates user and initiates OAuth2.0 authorization flow for third-party integrations
     * Calls backend /oauth2/login endpoint with credentials and OAuth2 protocol parameters
     */
    const handleExternalAuthLogin = async () => {
        // Step 1: Call external auth login API with OAuth2 protocol parameters
        // Note: camelCase params are converted to underscore format as per OAuth2 spec
        const externalAuthResponse = await externalAuthService.externalAuthLogin({
            email,
            password,
            ...externalAuthParams
        });

        // Expected response: { session_id, userId, message, token, hasIdentity }
        const sessionId = externalAuthResponse.session_id || externalAuthResponse.redirectUrl?.match(/session_id=([^&]+)/)?.[1];

        if (!sessionId) {
            throw new Error('Invalid response from external auth login API');
        }

        // Store session_id in localStorage for use in authorization grant page
        Storage.local.setItem(LOCAL_STORAGE.EXTERNAL_AUTH_SESSION_ID, sessionId);

        // Step 2: Complete standard Sephora login flow (generateTokens, profile fetch, etc.)
        // This ensures the user is fully authenticated before proceeding to authorization
        const successCallback = () => {
            // After successful login, navigate to authorize page
            window.location.href = `/authorize?session_id=${sessionId}`;
        };

        await store.dispatch(
            store.dispatch(
                UserActions.signIn(
                    email,
                    password,
                    !!Sephora.isMobile(),
                    false,
                    successCallback,
                    json => {
                        if (json.errorMessages) {
                            throw new Error({ errorMessage: json.errorMessages[0] });
                        }
                    },
                    false,
                    null,
                    false,
                    false,
                    false,
                    false
                )
            )
        );
    };

    const handleContinueAsGuest = () => {
        if (isOAuth2Flow) {
            // For OAuth2 external auth flow, guest access is not available
            setErrorMessages([{ errorMessage: 'Guest access is not available for external authentication. Please sign in to continue.' }]);
        } else {
            // For regular login, redirect to home page
            window.location.href = '/';
        }
    };

    const handleClose = () => {
        if (externalAuthParams.redirectUri) {
            // If there's a redirect URI (OAuth flow), redirect there
            window.location.href = externalAuthParams.redirectUri;
        } else {
            // Otherwise, go back to previous page
            window.history.back();
        }
    };

    if (userUtils.isSignedIn()) {
        // TODO: Backend scenario for already logged-in users is still in progress
        // When complete, backend should handle OAuth2 flow for authenticated users
        // For now, redirect already logged-in users to home page
        window.location.href = '/';

        return null; // or a loading spinner, since useEffect will redirect
    }

    return (
        <div>
            <Box
                minHeight='100vh'
                display='flex'
                flexDirection='column'
                alignItems='center'
                justifyContent='flex-start'
                css={{
                    paddingLeft: '12px',
                    paddingRight: '12px'
                }}
            >
                <Box
                    css={{
                        position: 'relative',
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        borderBottom: '1px solid #EEEEEE'
                    }}
                >
                    {/* Back Button */}
                    <Box
                        css={{
                            position: 'absolute',
                            top: '26px',
                            left: '20px',
                            cursor: 'pointer'
                        }}
                        onClick={handleClose}
                    >
                        <Chevron
                            direction={'left'}
                            color='#000000'
                            css={{
                                width: '16px',
                                height: '16px'
                            }}
                        />
                    </Box>

                    {/* Logo */}
                    <Box css={{ marginBottom: '26px', marginTop: '27px' }}>
                        <img
                            src='/img/ufe/logo.svg'
                            alt='Sephora'
                            style={{ height: '26px', width: 'auto' }}
                        />
                    </Box>
                </Box>
                <Box css={{ marginTop: '60px' }}>
                    {/* Page Title */}
                    <Box
                        maxWidth='400px'
                        width='100%'
                        css={{ marginBottom: '32px' }}
                    >
                        <Text
                            is='h1'
                            fontSize='lg'
                            fontWeight='bold'
                            lineHeight='tight'
                        >
                            {getText('pageTitle')}
                        </Text>
                    </Box>

                    {/* Benefits Section */}
                    <Box
                        maxWidth='400px'
                        width='100%'
                        css={{ marginBottom: '40px' }}
                    >
                        <Grid
                            gridTemplateColumns='repeat(3, 1fr)'
                            css={{ gap: '16px' }}
                        >
                            <Box
                                display='flex'
                                flexDirection='column'
                                alignItems='center'
                                textAlign='center'
                            >
                                <Box>
                                    <img
                                        src='/img/ufe/auth/makeup.svg'
                                        alt='makeup'
                                        style={{ height: '60px', width: 'auto' }}
                                    />
                                </Box>
                                <Text
                                    fontSize='sm'
                                    lineHeight='tight'
                                    color='black'
                                >
                                    {getText('personalizedBeautyTitle')}
                                </Text>
                            </Box>
                            <Box
                                display='flex'
                                flexDirection='column'
                                alignItems='center'
                                textAlign='center'
                            >
                                <Box>
                                    <img
                                        src='/img/ufe/auth/earn-points.svg'
                                        alt='earn points'
                                        style={{ height: '60px', width: 'auto' }}
                                    />
                                </Box>
                                <Text
                                    fontSize='sm'
                                    lineHeight='tight'
                                    color='black'
                                >
                                    {getText('perksTitle')}
                                </Text>
                            </Box>
                            <Box
                                display='flex'
                                flexDirection='column'
                                alignItems='center'
                                textAlign='center'
                            >
                                <Box>
                                    <img
                                        src='/img/ufe/auth/chat.svg'
                                        alt='chat'
                                        style={{ height: '60px', width: 'auto' }}
                                    />
                                </Box>
                                <Text
                                    fontSize='sm'
                                    lineHeight='tight'
                                    color='black'
                                >
                                    {getText('adviceTitle')}
                                </Text>
                            </Box>
                        </Grid>
                    </Box>

                    {/* Form Container with 400px max-width */}
                    <Box
                        maxWidth='400px'
                        width='100%'
                    >
                        {/* Error Messages */}
                        {errorMessages.length > 0 && (
                            <Box css={{ marginBottom: '16px' }}>
                                <ErrorList errors={errorMessages} />
                            </Box>
                        )}

                        {/* Login Form */}
                        <form onSubmit={handleSubmit}>
                            <Box css={{ marginBottom: '16px' }}>
                                <InputEmail
                                    ref={emailInput}
                                    value={email}
                                    onChange={handleEmailChange}
                                    placeholder={getText('emailPlaceholder')}
                                    autoFocus={!email}
                                />
                            </Box>

                            <Box css={{ marginBottom: '8px' }}>
                                <PasswordRevealInput
                                    ref={passwordInput}
                                    value={password}
                                    onChange={handlePasswordChange}
                                    placeholder={getText('passwordPlaceholder')}
                                />
                            </Box>

                            {/* Forgot Password Link */}
                            <Box
                                css={{ marginBottom: '24px' }}
                                textAlign='right'
                            >
                                <Link
                                    href='/resetpassword'
                                    color='blue'
                                    fontSize='sm'
                                >
                                    {getText('forgotPassword')}
                                </Link>
                            </Box>

                            {/* Terms and Privacy */}
                            <Box css={{ marginBottom: '12px' }}>
                                <Text
                                    fontSize='xs'
                                    color='gray'
                                    lineHeight='base'
                                >
                                    {getText('termsPrefix')}{' '}
                                    <Link
                                        href='/terms-of-use'
                                        target='_blank'
                                        color='blue'
                                        textDecoration='none'
                                        fontWeight='bold'
                                    >
                                        {getText('termsOfUse')}
                                    </Link>{' '}
                                    {getText('termsAnd')}{' '}
                                    <Link
                                        href='/privacy-policy'
                                        target='_blank'
                                        color='blue'
                                        textDecoration='none'
                                    >
                                        {getText('privacyPolicy')}
                                    </Link>
                                </Text>
                            </Box>

                            {/* Sign In Button */}
                            <Button
                                type='submit'
                                width='100%'
                                variant='primary'
                                paddingY={3}
                            >
                                {getText('signInButton')}
                            </Button>

                            {/* Divider with spacing */}
                            <Box css={{ marginTop: '16px', marginBottom: '16px', height: '8px' }}>
                                <Divider css={{ height: '8px' }} />
                            </Box>

                            {/* Continue as Guest Button */}
                            <Button
                                type='button'
                                width='100%'
                                variant='secondary'
                                onClick={handleContinueAsGuest}
                                paddingY={3}
                            >
                                {getText('continueAsGuest')}
                            </Button>
                        </form>
                    </Box>
                </Box>
                {/* End Form Container */}
            </Box>
        </div>
    );
};

export default wrapFunctionalComponent(Login, 'Login');
